// Integrity and ordering rules for the installed card data (Codex r1 #3, #4):
// never install a version older than the installed one, and never treat a
// broken install (missing cards, a stale parser, an index that does not fit
// its cards) as ready. Pure, so the worker and the tests share them.
import { INDEX_FORMAT, SUPPORTED_PARSER, SUPPORTED_SCHEMA } from './offlineCards.codes'
import type { PersistedIndex, WordIndex } from './engine'
import type { InstalledMeta } from './store'
import type { A2Manifest } from './types'

/** The manifest this app can read. */
export function manifestSupported(m: A2Manifest | null | undefined): boolean {
  return !!m && m.schema_version === SUPPORTED_SCHEMA && m.parser_version === SUPPORTED_PARSER
}

/** `candidate` was built before `installed` (by the manifest's built_at). */
export function isOlderThan(candidate: A2Manifest, installed: A2Manifest): boolean {
  const a = Date.parse(candidate.built_at)
  const b = Date.parse(installed.built_at)
  return Number.isFinite(a) && Number.isFinite(b) && a < b
}

/** Installed metadata this app can load (schema, parser, a version). */
export function installedReadable(meta: InstalledMeta | null): meta is InstalledMeta {
  return !!meta && !!meta.version && manifestSupported(meta.manifest) && meta.manifest.version === meta.version && meta.card_count > 0
}

function wordIndexFits(w: WordIndex | undefined, cards: number): boolean {
  if (!w || typeof w.vocab !== 'string' || !(w.offsets instanceof Uint32Array) || !(w.postings instanceof Uint32Array)) return false
  const words = w.vocab ? w.vocab.split('\n').length : 0
  if (w.offsets.length !== words + 1 || w.offsets[words] !== w.postings.length) return false
  for (let i = 0; i < w.postings.length; i++) if (w.postings[i] >= cards) return false
  return true
}

/** The stored index belongs to `cards` cards and has this app's layout. */
export function indexFits(index: PersistedIndex | null | undefined, cards: number): boolean {
  if (!index || index.format !== INDEX_FORMAT) return false
  if (!wordIndexFits(index.name, cards) || !wordIndexFits(index.type, cards) || !wordIndexFits(index.oracle, cards)) return false
  if (!(index.nameKeyLine instanceof Uint32Array) || typeof index.nameKeys !== 'string') return false
  const keys = index.nameKeys ? index.nameKeys.split('\n').length : 0
  if (keys !== index.nameKeyLine.length) return false
  for (let i = 0; i < index.nameKeyLine.length; i++) if (index.nameKeyLine[i] >= cards) return false
  return true
}
